import React from 'react';
import Text from '@/components/Text';
import ListItem from '@/common/ListItem';
import clsx from 'clsx';

export interface IIndustryListItemProps {
  name: string;
  slug: string;
  description: string;
  isSelected: boolean;
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
  onPress: () => void;
}

// Get 2-letter initials from industry name
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    // Take first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  } else {
    // Take first 2 letters of single word
    return name.substring(0, 2).toUpperCase();
  }
};

// Get a consistent color for each industry based on slug
const getIndustryColor = (slug: string): string => {
  const colorOptions = [
    'bg-blue',
    'bg-purple',
    'bg-pink',
    'bg-success',
    'bg-warning',
    'bg-blue-600',
    'bg-purple-600',
    'bg-pink-600',
  ];
  
  // Use hash of slug to deterministically assign color
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colorOptions[Math.abs(hash) % colorOptions.length];
};

export default function IndustryListItem(props: IIndustryListItemProps) {
  const { name, slug, description, isSelected, isFirst, isLast, onPress } = props;
  
  const initials = getInitials(name);
  const colorClass = getIndustryColor(slug);
  
  // Truncate text to fixed character length for consistent width
  // Shorter on mobile, longer on desktop
  const truncatedName = name.length > 20 ? name.substring(0, 20) + '...' : name;
  const truncatedDescriptionMobile = description.length > 25 ? description.substring(0, 25) + '...' : description;
  const truncatedDescriptionDesktop = description.length > 40 ? description.substring(0, 40) + '...' : description;

  return (
    <ListItem 
      onClick={onPress} 
      className={clsx(
        "cursor-pointer px-4 md:px-6 py-2.5 md:py-3 transition-colors hover:bg-white/5",
      )}
    >
      <ListItem.Left className="flex-1 min-w-0 pr-4">
        <div
          className={clsx(
            "flex h-10 w-10 md:h-12 md:w-12 rounded-full flex-shrink-0 items-center justify-center text-white font-bold text-sm md:text-md shadow-inner",
            colorClass
          )}
        >
          {initials}
        </div>
        <div className="flex flex-col min-w-0 ml-2 overflow-hidden flex-1">
          <Text size="md" color="primary" className="truncate text-sm md:text-md">
            {truncatedName}
          </Text>
          <Text size="sm" color="secondary" className="truncate opacity-70 text-xs md:text-sm">
            <span className="md:hidden">{truncatedDescriptionMobile}</span>
            <span className="hidden md:inline">{truncatedDescriptionDesktop}</span>
          </Text>
        </div>
      </ListItem.Left>

      <ListItem.Right className="flex-none w-7 md:w-9 gap-2 md:gap-4">
        <div
          className={clsx(
            "h-5 w-5 rounded-full border-2 transition-all flex items-center justify-center flex-none",
            isSelected ? "border-success bg-success/20" : "border-line-primary"
          )}
        >
          {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-success" />}
        </div>
      </ListItem.Right>
    </ListItem>
  );
}
